import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './NewsPage.css';

interface NewsItem {
  id: number;
  name: string;
  date: string;
  category: string;
  title: {
    en: string;
    zh: string;
    ja?: string;
  };
  summary: {
    en: string;
    zh: string;
    ja?: string;
  };
  content: {
    en: string;
    zh: string;
    ja?: string;
  };
  image: string;
}

const NewsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch('/news.json');
        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }
        const data = await response.json();
        setNews(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Error fetching news:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const getLocalizedText = (obj: { en: string; zh: string; ja?: string }): string => {
    const lang = i18n.language;
    if (lang === 'zh' || lang === 'zh-TW') {
      return obj.zh;
    }
    if (lang === 'ja' && obj.ja) {
      return obj.ja;
    }
    return obj.en;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const locale = i18n.language === 'zh' || i18n.language === 'zh-TW'
      ? 'zh-TW'
      : i18n.language === 'ja'
      ? 'ja-JP'
      : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category: string): string => {
    return t(`news.categories.${category}`, category);
  };

  if (loading) {
    return (
      <div className="news-page">
        <section className="page-header">
          <div className="container">
            <h1 className="page-title">{t('news.title')}</h1>
            <p className="page-subtitle">{t('news.subtitle')}</p>
          </div>
        </section>
        <section className="news-content">
          <div className="container">
            <div className="loading-state">
              <p>{t('common.loading')}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-page">
        <section className="page-header">
          <div className="container">
            <h1 className="page-title">{t('news.title')}</h1>
            <p className="page-subtitle">{t('news.subtitle')}</p>
          </div>
        </section>
        <section className="news-content">
          <div className="container">
            <div className="error-state">
              <p>{t('common.error')}: {error}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="news-page">
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">{t('news.title')}</h1>
          <p className="page-subtitle">{t('news.subtitle')}</p>
        </div>
      </section>

      <section className="news-content">
        <div className="container">
          <div className="news-list">
            {news.map((item) => (
              <Link 
                key={item.id} 
                to={`/news/${item.name}`}
                className="news-card"
              >
                <div className="news-card-image">
                  {item.image && (
                    <img src={item.image} alt={getLocalizedText(item.title)} />
                  )}
                </div>
                <div className="news-card-content">
                  <div className="news-card-header">
                    <span className="news-category-badge">{getCategoryLabel(item.category)}</span>
                    <time className="news-date">{formatDate(item.date)}</time>
                  </div>
                  <h3 className="news-card-title">{getLocalizedText(item.title)}</h3>
                  <p className="news-card-summary">{getLocalizedText(item.summary)}</p>
                  <span className="news-read-more">
                    {t('news.readMore')} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsPage;

